/**
 * LMS Phase C — Lesson Plans + Syllabus Progress panels.
 * Extracted from SchoolLMS.jsx.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Loader2, Plus, BookMarked, TrendingUp, CheckSquare } from 'lucide-react';
import { schoolApi as api } from '@/components/school/schoolApi';

const LessonPlansTab = ({ tenantId, subjects = [] }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lesson_id: '', learning_outcomes: '', teaching_aids: '', resources: '', assessment_method: 'quiz', class_mode: 'physical', quiz_id: '', planned_date: '', estimated_minutes: 45, prerequisites: '' });
  const [filterSubject, setFilterSubject] = useState('');
  const { data: plans = [] } = useQuery({ queryKey: ['lesson-plans', tenantId, filterSubject], queryFn: () => api(`/tenants/${tenantId}/lesson-plans${filterSubject ? `?subject_id=${filterSubject}` : ''}`) });
  const { data: lessons = [] } = useQuery({ queryKey: ['all-lessons-for-plans', tenantId], queryFn: () => api(`/tenants/${tenantId}/lessons`) });
  const { data: quizzes = [] } = useQuery({ queryKey: ['all-quizzes-for-plans', tenantId], queryFn: () => api(`/tenants/${tenantId}/quizzes`) });

  const saveMut = useMutation({
    mutationFn: (body) => api(`/tenants/${tenantId}/lesson-plans`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { toast.success('Lesson plan saved'); setOpen(false); qc.invalidateQueries({ queryKey: ['lesson-plans', tenantId] }); },
    onError: e => toast.error(e.message),
  });
  const taughtMut = useMutation({
    mutationFn: (lesson_id) => api(`/tenants/${tenantId}/lesson-plans/${lesson_id}/mark-taught`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => { toast.success('Marked as taught'); qc.invalidateQueries({ queryKey: ['lesson-plans', tenantId] }); },
    onError: e => toast.error(e.message),
  });

  const submit = () => {
    saveMut.mutate({
      lesson_id: form.lesson_id,
      learning_outcomes: form.learning_outcomes.split('\n').map(s => s.trim()).filter(Boolean),
      teaching_aids: form.teaching_aids.split(',').map(s => s.trim()).filter(Boolean),
      resources: form.resources.split('\n').map(s => s.trim()).filter(Boolean),
      assessment_method: form.assessment_method,
      class_mode: form.class_mode,
      quiz_id: form.quiz_id || null,
      planned_date: form.planned_date || null,
      estimated_minutes: Number(form.estimated_minutes) || 45,
      prerequisites: form.prerequisites.split(',').map(s => s.trim()).filter(Boolean),
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><BookMarked className="h-5 w-5 text-indigo-600" /> Lesson Plans</CardTitle>
          <CardDescription>Enrich each lesson with learning outcomes, teaching aids, resources & assessment method.</CardDescription>
        </div>
        <div className="flex gap-2">
          <Select value={filterSubject || 'all'} onValueChange={(v) => setFilterSubject(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48" data-testid="plans-subject-filter"><SelectValue placeholder="All subjects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All subjects</SelectItem>
              {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setOpen(true)} data-testid="new-lesson-plan-btn"><Plus className="h-4 w-4 mr-1" /> New Plan</Button>
        </div>
      </CardHeader>
      <CardContent>
        {plans.length === 0 ? <p className="text-sm text-slate-500 text-center py-6" data-testid="lesson-plans-empty">No lesson plans yet.</p> :
         <Table>
          <TableHeader><TableRow><TableHead>Lesson</TableHead><TableHead>Mode</TableHead><TableHead>Assessment</TableHead><TableHead>Planned</TableHead><TableHead>Outcomes</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
           {plans.map(p => (
            <TableRow key={p.id} data-testid={`plan-row-${p.lesson_id}`}>
              <TableCell className="font-medium">{p.lesson_title || p.lesson_id}</TableCell>
              <TableCell><Badge variant="outline">{p.class_mode}</Badge></TableCell>
              <TableCell><Badge variant="secondary">{p.assessment_method || '—'}</Badge></TableCell>
              <TableCell className="text-xs">{p.planned_date || '—'}</TableCell>
              <TableCell className="text-xs">{(p.learning_outcomes || []).slice(0, 2).join(', ')}{(p.learning_outcomes || []).length > 2 && '…'}</TableCell>
              <TableCell>{p.is_taught ? <Badge className="bg-emerald-100 text-emerald-700">Taught</Badge> : <Badge variant="outline">Pending</Badge>}</TableCell>
              <TableCell className="text-right">
                {!p.is_taught && <Button size="sm" variant="outline" onClick={() => taughtMut.mutate(p.lesson_id)} data-testid={`mark-taught-${p.lesson_id}`}><CheckSquare className="h-3.5 w-3.5 mr-1" /> Mark Taught</Button>}
              </TableCell>
            </TableRow>
           ))}
          </TableBody>
         </Table>}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New / Update Lesson Plan</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Lesson *</Label>
              <Select value={form.lesson_id} onValueChange={(v) => setForm({ ...form, lesson_id: v })}>
                <SelectTrigger data-testid="plan-lesson-select"><SelectValue placeholder="Select lesson" /></SelectTrigger>
                <SelectContent>{lessons.map(l => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Class Mode</Label>
              <Select value={form.class_mode} onValueChange={(v) => setForm({ ...form, class_mode: v })}>
                <SelectTrigger data-testid="plan-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="physical">Physical</SelectItem><SelectItem value="online">Online</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Assessment Method</Label>
              <Select value={form.assessment_method} onValueChange={(v) => setForm({ ...form, assessment_method: v })}>
                <SelectTrigger data-testid="plan-assessment-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="quiz">Quiz</SelectItem><SelectItem value="oral">Oral</SelectItem><SelectItem value="written">Written</SelectItem><SelectItem value="project">Project</SelectItem><SelectItem value="viva">Viva</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Planned Date</Label><Input type="date" value={form.planned_date} onChange={(e) => setForm({ ...form, planned_date: e.target.value })} data-testid="plan-date-input" /></div>
            <div><Label>Estimated Minutes</Label><Input type="number" value={form.estimated_minutes} onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })} data-testid="plan-minutes-input" /></div>
            <div className="md:col-span-2"><Label>Learning Outcomes (one per line)</Label><Textarea rows={3} value={form.learning_outcomes} onChange={(e) => setForm({ ...form, learning_outcomes: e.target.value })} data-testid="plan-outcomes-input" placeholder="Students will…" /></div>
            <div className="md:col-span-2"><Label>Teaching Aids (comma-separated)</Label><Input value={form.teaching_aids} onChange={(e) => setForm({ ...form, teaching_aids: e.target.value })} data-testid="plan-aids-input" placeholder="Whiteboard, Projector, Worksheet" /></div>
            <div className="md:col-span-2"><Label>Resources (one URL per line)</Label><Textarea rows={2} value={form.resources} onChange={(e) => setForm({ ...form, resources: e.target.value })} data-testid="plan-resources-input" /></div>
            <div><Label>Link to Quiz (optional)</Label>
              <Select value={form.quiz_id || 'none'} onValueChange={(v) => setForm({ ...form, quiz_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="plan-quiz-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{quizzes.map(q => <SelectItem key={q.id} value={q.id}>{q.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Prerequisites (comma-separated)</Label><Input value={form.prerequisites} onChange={(e) => setForm({ ...form, prerequisites: e.target.value })} data-testid="plan-prereq-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!form.lesson_id || saveMut.isPending} data-testid="plan-save-btn">
              {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ==================== PHASE C — SYLLABUS PROGRESS TAB ====================
const SyllabusProgressTab = ({ tenantId, subjects = [], classes = [] }) => {
  const [subjectId, setSubjectId] = useState('');
  const [classId, setClassId] = useState('');
  const qs = [subjectId && `subject_id=${subjectId}`, classId && `class_id=${classId}`].filter(Boolean).join('&');
  const { data, isLoading } = useQuery({
    queryKey: ['syllabus-progress', tenantId, subjectId, classId],
    queryFn: () => api(`/tenants/${tenantId}/syllabus-progress${qs ? '?' + qs : ''}`),
  });
  const { data: byTeacher = [] } = useQuery({
    queryKey: ['syllabus-by-teacher', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/syllabus-progress/by-teacher`),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-emerald-600" /> Syllabus Progress</CardTitle>
            <CardDescription>Planned vs actually taught lessons — updated when teachers mark lessons taught.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={subjectId || 'all'} onValueChange={(v) => setSubjectId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40" data-testid="syllabus-subject-filter"><SelectValue placeholder="All subjects" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All subjects</SelectItem>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={classId || 'all'} onValueChange={(v) => setClassId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40" data-testid="syllabus-class-filter"><SelectValue placeholder="All classes" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All classes</SelectItem>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
            <>
              <div className="grid md:grid-cols-3 gap-3 mb-4">
                <div className="p-4 rounded-lg border bg-gradient-to-br from-emerald-50 to-emerald-100"><div className="text-xs text-emerald-700 font-medium">Overall Completion</div><div className="text-3xl font-bold text-emerald-900" data-testid="syllabus-overall-percent">{data?.overall_completion_percent || 0}%</div></div>
                <div className="p-4 rounded-lg border bg-gradient-to-br from-indigo-50 to-indigo-100"><div className="text-xs text-indigo-700 font-medium">Total Lessons</div><div className="text-3xl font-bold text-indigo-900" data-testid="syllabus-total">{data?.total_lessons || 0}</div></div>
                <div className="p-4 rounded-lg border bg-gradient-to-br from-amber-50 to-amber-100"><div className="text-xs text-amber-700 font-medium">Taught</div><div className="text-3xl font-bold text-amber-900" data-testid="syllabus-taught">{data?.total_taught || 0}</div></div>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Subject</TableHead><TableHead>Total</TableHead><TableHead>Planned</TableHead><TableHead>Taught</TableHead><TableHead>Missed</TableHead><TableHead>%</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(data?.by_subject || []).map(s => (
                    <TableRow key={s.subject_id} data-testid={`syllabus-subject-${s.subject_id}`}>
                      <TableCell className="font-medium">{s.subject_name}</TableCell>
                      <TableCell>{s.total}</TableCell>
                      <TableCell>{s.planned}</TableCell>
                      <TableCell>{s.taught}</TableCell>
                      <TableCell className={s.missed > 0 ? 'text-rose-600 font-medium' : ''}>{s.missed}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full ${s.completion_percent >= 80 ? 'bg-emerald-500' : s.completion_percent >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${s.completion_percent}%` }} />
                          </div>
                          <span className="text-xs font-medium">{s.completion_percent}%</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Teacher Completion Leaderboard</CardTitle></CardHeader>
        <CardContent>
          {byTeacher.length === 0 ? <p className="text-sm text-slate-500 text-center py-4" data-testid="teacher-leaderboard-empty">No data yet.</p> :
           <div className="space-y-2">
             {byTeacher.slice(0, 10).map((t, i) => (
               <div key={t.teacher_id} className="flex items-center justify-between p-2 rounded-md border bg-slate-50" data-testid={`teacher-leader-${i}`}>
                 <div className="flex items-center gap-2"><Badge variant="outline">#{i+1}</Badge><span className="text-sm font-medium">{t.teacher_name}</span></div>
                 <Badge className="bg-indigo-100 text-indigo-700">{t.taught_count} lessons</Badge>
               </div>
             ))}
           </div>}
        </CardContent>
      </Card>
    </div>
  );
};

export { LessonPlansTab, SyllabusProgressTab };
